/* eslint-disable */
// Programme management dialogs (iter-126c, slice 4 of EventDetail.jsx refactor).
// Extracts the 6 admin-only Dialog blocks that previously lived inline in
// the parent component (~510 LOC):
//
//   1. Add Interval / Break
//   2. Add TBC placeholder
//   3. Edit TBC placeholder title
//   4. Replace TBC placeholder with a real piece
//   5. Add Music (multi-select picker)
//   6. Re-order programme (numeric inputs)
//
// All Dialogs are controlled via `open` + `onOpenChange` props from the
// parent. The trigger buttons for #1/#2/#5 are kept inline in the parent
// (now plain <Button onClick={...}>) so the existing toolbar layout
// stays untouched.
import React from "react";
import { Link } from "react-router-dom";
import { Coffee, HelpCircle, Music, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";


export const ProgrammeDialogs = ({
  // Add Interval
  addIntervalOpen, setAddIntervalOpen,
  intervalLabel, setIntervalLabel,
  intervalDuration, setIntervalDuration,
  handleAddInterval,

  // Add TBC placeholder
  addPlaceholderOpen, setAddPlaceholderOpen,
  placeholderTitleDraft, setPlaceholderTitleDraft,
  handleAddPlaceholder,

  // Edit TBC placeholder
  editPlaceholderOpen, setEditPlaceholderOpen,
  editingPlaceholder, setEditingPlaceholder,
  handleUpdatePlaceholder,

  // Replace TBC placeholder
  replacePlaceholderOpen, setReplacePlaceholderOpen,
  setReplacingPlaceholderId,
  replacePickerSelection, setReplacePickerSelection,
  replacePickerSearch, setReplacePickerSearch,
  availableMusic,
  replacingPlaceholder,
  handleConfirmReplacePlaceholder,

  // Add Music
  addMusicOpen, setAddMusicOpen,
  musicPickerSelection, setMusicPickerSelection,
  musicPickerSearch, setMusicPickerSearch,
  event,
  addingMusic,
  handleAddMusic,

  // Re-order programme
  reorderOpen, setReorderOpen,
  reorderDraft, setReorderDraft,
  reorderBusy, savingReorder,
  handleSaveReorder,
}) => (
  <>
    {/* 1. Add Interval / Break */}
    <Dialog open={addIntervalOpen} onOpenChange={setAddIntervalOpen}>
      <DialogContent className="glass border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Add Interval / Break
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="interval-label" className="text-xs uppercase tracking-wider text-muted-foreground">
              Label
            </Label>
            <Input
              id="interval-label"
              value={intervalLabel}
              onChange={(e) => setIntervalLabel(e.target.value)}
              placeholder="Interval"
              maxLength={60}
              className="mt-1.5"
              data-testid="interval-label-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use anything: &ldquo;Interval&rdquo;, &ldquo;Speeches&rdquo;, &ldquo;Awards&rdquo;, etc.
            </p>
          </div>
          <div>
            <Label htmlFor="interval-duration" className="text-xs uppercase tracking-wider text-muted-foreground">
              Duration (min) — optional
            </Label>
            <Input
              id="interval-duration"
              type="number"
              min="1"
              max="240"
              value={intervalDuration}
              onChange={(e) => setIntervalDuration(e.target.value)}
              placeholder="e.g. 20"
              className="mt-1.5"
              data-testid="interval-duration-input"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setAddIntervalOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary text-primary-foreground"
              onClick={handleAddInterval}
              data-testid="confirm-add-interval-btn"
            >
              Add to Programme
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* 2. Add TBC placeholder (iter-94) */}
    <Dialog open={addPlaceholderOpen} onOpenChange={(o) => {
      setAddPlaceholderOpen(o);
      if (!o) setPlaceholderTitleDraft("");
    }}>
      <DialogContent className="glass border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Add a TBC piece
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="placeholder-title" className="text-xs uppercase tracking-wider text-muted-foreground">
              Title
            </Label>
            <Input
              id="placeholder-title"
              value={placeholderTitleDraft}
              onChange={(e) => setPlaceholderTitleDraft(e.target.value)}
              placeholder="e.g. Cornet Solo - TBC"
              maxLength={200}
              className="mt-1.5"
              data-testid="placeholder-title-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddPlaceholder();
              }}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use this for slots you haven&apos;t decided yet. You can edit the title or swap it for a real piece later.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setAddPlaceholderOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary text-primary-foreground"
              onClick={handleAddPlaceholder}
              data-testid="confirm-add-placeholder-btn"
            >
              Add to Programme
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* 3. Edit TBC placeholder title (iter-94) */}
    <Dialog open={editPlaceholderOpen} onOpenChange={(o) => {
      setEditPlaceholderOpen(o);
      if (!o) setEditingPlaceholder(null);
    }}>
      <DialogContent className="glass border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Edit TBC title
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="edit-placeholder-title" className="text-xs uppercase tracking-wider text-muted-foreground">
              Title
            </Label>
            <Input
              id="edit-placeholder-title"
              value={editingPlaceholder?.title ?? ""}
              onChange={(e) =>
                setEditingPlaceholder((p) => (p ? { ...p, title: e.target.value } : p))
              }
              maxLength={200}
              className="mt-1.5"
              data-testid="edit-placeholder-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdatePlaceholder();
              }}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setEditPlaceholderOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary text-primary-foreground"
              onClick={handleUpdatePlaceholder}
              data-testid="confirm-edit-placeholder-btn"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* 4. Replace TBC placeholder with a real piece (iter-94) */}
    <Dialog open={replacePlaceholderOpen} onOpenChange={(o) => {
      setReplacePlaceholderOpen(o);
      if (!o) {
        setReplacingPlaceholderId(null);
        setReplacePickerSelection("");
        setReplacePickerSearch("");
      }
    }}>
      <DialogContent className="glass border-white/10 max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Replace TBC piece
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Pick a piece from the library to swap into this slot. The TBC entry will keep its position in the running order.
        </p>
        <div className="mt-3">
          <Input
            placeholder="Search title, composer, arranger…"
            value={replacePickerSearch}
            onChange={(e) => setReplacePickerSearch(e.target.value)}
            data-testid="replace-placeholder-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-1.5 mt-3 min-h-[200px]">
          {availableMusic
            .filter((m) => {
              const q = replacePickerSearch.trim().toLowerCase();
              if (!q) return true;
              return (
                (m.title || "").toLowerCase().includes(q) ||
                (m.composer || "").toLowerCase().includes(q) ||
                (m.arranger || "").toLowerCase().includes(q)
              );
            })
            .map((m) => {
              const isSelected = replacePickerSelection === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setReplacePickerSelection(m.id)}
                  className={`w-full text-left p-2.5 rounded-md transition-colors border ${
                    isSelected
                      ? "bg-primary/10 border-primary/40"
                      : "bg-secondary/40 border-transparent hover:bg-secondary/60"
                  }`}
                  data-testid={`replace-placeholder-row-${m.id}`}
                >
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {m.composer || "—"}
                    {m.arranger ? ` (arr. ${m.arranger})` : ""}
                  </p>
                </button>
              );
            })}
          {availableMusic.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No more pieces available — every piece in the library is already on this programme.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-white/5">
          <Button variant="ghost" onClick={() => setReplacePlaceholderOpen(false)}>Cancel</Button>
          <Button
            className="bg-primary text-primary-foreground"
            onClick={handleConfirmReplacePlaceholder}
            disabled={!replacePickerSelection || replacingPlaceholder}
            data-testid="confirm-replace-placeholder-btn"
          >
            {replacingPlaceholder ? "Replacing…" : "Replace"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* 5. Add Music — multi-select picker */}
    <Dialog open={addMusicOpen} onOpenChange={(o) => {
      setAddMusicOpen(o);
      if (!o) {
        setMusicPickerSelection(new Set());
        setMusicPickerSearch("");
      }
    }}>
      <DialogContent className="glass border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Add Music to Event
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {availableMusic.length > 0 ? (
            <>
              {event?.event_type === "concert" && (
                <p className="text-xs text-muted-foreground" data-testid="concert-music-pads-hint">
                  Showing pieces currently in the pads only. Hand more out from the Music Library if you need them here.
                </p>
              )}
              <Input
                value={musicPickerSearch}
                onChange={(e) => setMusicPickerSearch(e.target.value)}
                placeholder="Search title or composer…"
                className="bg-secondary/50"
                data-testid="music-picker-search"
              />
              <ScrollArea className="h-72 rounded-lg border border-white/5 bg-secondary/20">
                <div className="p-2 space-y-1">
                  {availableMusic
                    .filter((p) => {
                      const q = musicPickerSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (p.title || "").toLowerCase().includes(q) ||
                        (p.composer || "").toLowerCase().includes(q) ||
                        (p.arranger || "").toLowerCase().includes(q)
                      );
                    })
                    .map((piece) => {
                      const checked = musicPickerSelection.has(piece.id);
                      return (
                        <label
                          key={piece.id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${checked ? "bg-primary/15" : "hover:bg-secondary/40"}`}
                          data-testid={`music-picker-row-${piece.id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setMusicPickerSelection((prev) => {
                                const next = new Set(prev);
                                if (v) next.add(piece.id);
                                else next.delete(piece.id);
                                return next;
                              });
                            }}
                            data-testid={`music-picker-check-${piece.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{piece.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {piece.composer}
                              {piece.arranger ? ` (arr. ${piece.arranger})` : ""}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                </div>
              </ScrollArea>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="music-picker-selected-count">
                  {musicPickerSelection.size} selected
                </span>
                {musicPickerSelection.size > 0 && (
                  <button
                    type="button"
                    className="underline hover:text-foreground"
                    onClick={() => setMusicPickerSelection(new Set())}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setAddMusicOpen(false)}>Cancel</Button>
                <Button
                  className="bg-primary text-primary-foreground"
                  onClick={handleAddMusic}
                  disabled={addingMusic || musicPickerSelection.size === 0}
                  data-testid="confirm-add-music-btn"
                >
                  {addingMusic ? "Adding…" : `Add ${musicPickerSelection.size || ""} to Event`}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
              {event?.event_type === "concert" ? (
                <>
                  <p className="font-medium">No music is currently in the pads</p>
                  <p className="text-xs mt-2">
                    Concert programmes are built from pieces marked &quot;in pads&quot;. Open the Music Library, select the pieces you want available, and hand them out to the pads first.
                  </p>
                </>
              ) : (
                <p>No music available to add</p>
              )}
              <Link to="/music">
                <Button variant="outline" className="mt-4">
                  Go to Music Library
                </Button>
              </Link>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* 6. Re-order programme — numeric input dialog (iter-82) */}
    <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
      <DialogContent className="glass border-white/10 max-w-md max-h-[85vh] flex flex-col" data-testid="reorder-programme-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Re-order programme</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Type the position you want each piece to take, then save. Pieces will be sorted by the numbers you enter (ties keep their current order).
        </p>
        <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-2 mt-3">
          {reorderDraft.map((row, idx) => (
            <div
              key={row.id}
              className={`flex items-center gap-3 p-3 rounded-md ${row.isInterval ? "bg-amber-500/10 border border-amber-500/20" : "bg-secondary/40"}`}
              data-testid={`reorder-row-${row.id}`}
            >
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={reorderDraft.length}
                value={row.pos}
                onChange={(e) => {
                  const newVal = e.target.value;
                  setReorderDraft((prev) => {
                    const next = prev.slice();
                    next[idx] = { ...next[idx], pos: newVal };
                    return next;
                  });
                }}
                className="w-16 h-9 text-center font-mono"
                data-testid={`reorder-input-${row.id}`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {row.isInterval && <Coffee className="w-3 h-3 inline mr-1 text-amber-300" />}
                  {row.title}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between gap-2 mt-4 pt-3 border-t border-white/5">
          <Button
            variant="ghost"
            onClick={() => {
              // Reset numbering to 1..N matching current display order
              setReorderDraft((prev) => prev.map((r, i) => ({ ...r, pos: i + 1 })));
            }}
            data-testid="reorder-reset-btn"
          >
            Reset numbers
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setReorderOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveReorder}
              disabled={savingReorder || reorderBusy}
              data-testid="reorder-save-btn"
            >
              {savingReorder ? "Saving…" : "Save order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Re-exported icons so callers can render the same trigger buttons
        in the parent toolbar without a separate icon import. Components
        like <ProgrammeDialogs.TriggerIcons.Coffee /> aren't used today —
        the icons stay inline in the parent. This stub keeps the file
        from leaving the lucide imports as "unused" if a future refactor
        wants to centralise them. */}
  </>
);

export default ProgrammeDialogs;
